// Layout event bus — pub/sub primitive used by widgets to communicate without
// prop drilling. One bus per LayoutRenderer instance (i.e. per Case Client tab).
//
// Topic naming convention: xv.<Verb><Noun>  (mirrors ICM's icm.OpenCase etc.)
//
// Two subscribe paths:
//   1. Explicit:  bus.subscribe('xv.OpenCase', handler)
//   2. By convention: a widget exposes onXvOpenCase(payload) — the renderer
//      auto-subscribes the method to its xv.OpenCase topic on mount. This
//      mirrors ICM's onicm_OpenCase shorthand. (Convention wiring is applied
//      in LayoutRenderer; eventBus itself only knows about explicit subs.)

export function createEventBus() {
  const subs = new Map(); // topic -> Set<handler>

  function publish(topic, payload) {
    const set = subs.get(topic);
    if (!set || set.size === 0) return;
    // Snapshot so handlers can unsubscribe / publish during dispatch without
    // mutating the iterator.
    Array.from(set).forEach(h => {
      try { h(payload, topic); }
      catch (e) {
        // Don't let one bad handler break the bus. Real diagnostics surface
        // through WidgetErrorBoundary on the consumer side.
        if (typeof console !== 'undefined') console.error(`[layout-bus] ${topic}:`, e);
      }
    });
  }

  function subscribe(topic, handler) {
    if (typeof handler !== 'function') return () => {};
    let set = subs.get(topic);
    if (!set) { set = new Set(); subs.set(topic, set); }
    set.add(handler);
    return () => unsubscribe(topic, handler);
  }

  function unsubscribe(topic, handler) {
    const set = subs.get(topic);
    if (!set) return;
    set.delete(handler);
    if (set.size === 0) subs.delete(topic);
  }

  function allTopics() {
    return Array.from(subs.keys());
  }

  function clear() {
    subs.clear();
  }

  return { publish, subscribe, unsubscribe, allTopics, clear };
}

// Built-in topic catalog — kept here so the registry validator (and later the
// Script Widget editor's autocomplete) can enumerate them. Payload shapes are
// documented in CASEMATRIX_PRD.md §11.7.
export const BUILTIN_TOPICS = Object.freeze([
  'xv.OpenCase',
  'xv.OpenWorkItem',
  'xv.OpenInbox',
  'xv.AddCase',
  'xv.AddCaseSubmitted',
  'xv.AddCaseCancelled',
  'xv.AddTask',
  'xv.AddTaskSubmitted',
  'xv.AddTaskCancelled',
  'xv.CaseSelected',
  'xv.CaseCreated',
  'xv.CaseSaved',
  'xv.CaseStatusChanged',
  'xv.WorkItemClaimed',
  'xv.WorkItemReleased',
  'xv.WorkItemSaved',
  'xv.WorkItemCompleted',
  'xv.TaskLaunched',
  'xv.CommentAdded',
  'xv.DocumentUploaded',
  'xv.SearchCases',
  'xv.RefreshContext',
  'xv.ClosePage',
  'xv.ScriptResult',
]);
